import React, { useState, useEffect, useRef } from 'react'
import { Table, Button, Tag, Space, Card, message, Modal, Steps, Input, Select, Divider, Typography, Dropdown, MenuProps, Upload } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined, UserOutlined, FileTextOutlined, RocketOutlined, CheckCircleOutlined, DownOutlined, BgColorsOutlined, GiftOutlined, NotificationOutlined, UploadOutlined, ReloadOutlined, InboxOutlined, SettingOutlined } from '@ant-design/icons'
import { emailCampaignsApi, EmailCampaign, smtpApi, SMTPAccount } from '../../services/emailApi'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import SMTPSettings from './SMTPSettings'

const { Step } = Steps
const { TextArea } = Input
const { Title } = Typography
const { Dragger } = Upload


const quillStyles = `
  .custom-quill .ql-editor.ql-blank::before {
    color: #888 !important;
    font-style: italic;
    opacity: 1;
  }
  .custom-quill .ql-editor {
    min-height: 300px;
    font-size: 14px;
    color: #333;
    background: #fff;
  }
  .custom-quill .ql-toolbar {
    background: #fafafa;
    border-radius: 6px 6px 0 0;
  }
  .custom-quill .ql-container {
    border-radius: 0 0 6px 6px;
    background: #fff;
  }
`


interface WizardFormData {
    name: string
    subject: string
    smtp_account_id: number | undefined
    recipients: string
    body_html: string
}

const EmailCampaigns: React.FC = () => {

    const [campaigns, setCampaigns] = useState<EmailCampaign[]>([])
    const [smtpAccounts, setSmtpAccounts] = useState<SMTPAccount[]>([])
    const [loading, setLoading] = useState(false)
    const [creating, setCreating] = useState(false)


    const [isWizardVisible, setIsWizardVisible] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [isSmtpModalVisible, setIsSmtpModalVisible] = useState(false)


    const [formData, setFormData] = useState<WizardFormData>({
        name: '',
        subject: '',
        smtp_account_id: undefined,
        recipients: '',
        body_html: ''
    })


    const quillRef = useRef<any>(null)


    const modules = React.useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            [{ 'color': [] }, { 'background': [] }],
            ['clean']
        ],
    }), [])

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image', 'color', 'background'
    ]

    const templates: MenuProps['items'] = [
        {
            key: 'welcome',
            label: '🚀 Приветственное письмо',
            icon: <RocketOutlined />,
            onClick: () => applyTemplate('welcome')
        },
        {
            key: 'newsletter',
            label: '📰 Новостная рассылка',
            icon: <NotificationOutlined />,
            onClick: () => applyTemplate('newsletter')
        },
        {
            key: 'promo',
            label: '🎁 Промо-акция',
            icon: <GiftOutlined />,
            onClick: () => applyTemplate('promo')
        },
        {
            key: 'simple',
            label: '📝 Простое письмо',
            icon: <FileTextOutlined />,
            onClick: () => applyTemplate('simple')
        }
    ]

    const getEditorContent = (): string => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor()
            return editor.root.innerHTML
        }
        return ''
    }

    const setEditorContent = (html: string) => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor()
            editor.clipboard.dangerouslyPasteHTML(html)
        }
    }

    const applyTemplate = (type: string) => {
        let content = ''
        const date = new Date().toLocaleDateString('ru-RU')
        switch (type) {
            case 'welcome':
                content = `<div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 40px 20px; text-align: center; border-radius: 12px 12px 0 0;">
    <h1 style="color: white; margin: 0; font-size: 28px;">🚀 Добро пожаловать!</h1>
    <p style="color: rgba(255,255,255,0.9); margin-top: 10px;">Мы рады видеть вас</p>
  </div>
  <div style="background: #ffffff; padding: 30px; border: 1px solid #eee; border-top: none;">
    <p style="font-size: 16px; color: #333; line-height: 1.6;">Привет!</p>
    <p style="font-size: 16px; color: #333; line-height: 1.6;">Спасибо, что присоединились к нам.</p>
    <div style="text-align: center; margin: 30px 0;">
      <a href="#" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 14px 32px; text-decoration: none; border-radius: 25px; font-weight: 600; display: inline-block;">Начать работу →</a>
    </div>
  </div>
</div>`
                break
            case 'newsletter':
                content = `<div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: #1a1a2e; padding: 30px; text-align: center;">
    <h1 style="color: #fff; margin: 0; font-size: 24px;">📰 Еженедельный дайджест</h1>
    <p style="color: #aaa; margin: 10px 0 0;">${date}</p>
  </div>
  <div style="padding: 25px; background: white;">
    <h2 style="color: #1a1a2e; font-size: 20px; margin-bottom: 15px;">Главные новости</h2>
    <p style="color: #555; line-height: 1.7;">Здесь краткое описание новости.</p>
  </div>
</div>`
                break
            case 'promo':
                content = `<div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); padding: 50px 20px; text-align: center; border-radius: 12px 12px 0 0;">
    <h1 style="color: white; margin: 0; font-size: 42px; font-weight: 800;">🎁 СКИДКА 50%</h1>
    <p style="color: rgba(255,255,255,0.95); font-size: 18px; margin-top: 15px;">Только до конца недели!</p>
  </div>
  <div style="background: #ffffff; padding: 35px; text-align: center;">
    <p style="font-size: 18px; color: #333;">Используйте промокод: <b>PROMO2026</b></p>
  </div>
</div>`
                break
            case 'simple':
                content = `<div style="font-family: Arial, sans-serif; padding: 20px;">
  <p style="font-size: 16px; color: #333; line-height: 1.8;">Добрый день!</p>
  <p style="font-size: 16px; color: #333; line-height: 1.8;">Текст вашего письма...</p>
  <p style="font-size: 16px; color: #333; margin-top: 25px;">С уважением,<br/><strong>Ваше имя</strong></p>
</div>`
                break
        }
        setTimeout(() => {
            setEditorContent(content)
            setFormData(prev => ({ ...prev, body_html: content }))
        }, 100)
    }

    const fetchCampaigns = async () => {
        setLoading(true)
        try {
            const res = await emailCampaignsApi.getAll()
            setCampaigns(res.data)
        } catch (error) {
            message.error('Не удалось загрузить рассылки')
        } finally {
            setLoading(false)
        }
    }

    const fetchSmtpAccounts = async () => {
        try {
            const res = await smtpApi.getAll()
            setSmtpAccounts(res.data)
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        fetchCampaigns()
        fetchSmtpAccounts()
    }, [])

    const handleDelete = async (id: number) => {
        try {
            await emailCampaignsApi.delete(id)
            message.success('Рассылка удалена')
            fetchCampaigns()
        } catch (error) {
            message.error('Ошибка удаления')
        }
    }

    const handleStart = async (id: number) => {
        try {
            await emailCampaignsApi.start(id)
            message.success('Рассылка запущена!')
            fetchCampaigns()
        } catch (error: any) {
            message.error('Ошибка запуска: ' + (error.response?.data?.detail || error.message))
        }
    }

    const handleResend = async (id: number) => {
        Modal.confirm({
            title: <span className="text-white">Отправить повторно?</span>,
            content: <div className="text-gray-300">Все получатели получат письмо снова. Продолжить?</div>,
            okText: 'Отправить',
            cancelText: 'Отмена',
            onOk: async () => {
                try {
                    await emailCampaignsApi.resend(id)
                    message.success('Рассылка запущена повторно!')
                    fetchCampaigns()
                } catch (error: any) {
                    message.error('Ошибка: ' + (error.response?.data?.detail || error.message))
                }
            }
        })
    }


    const openWizard = (campaign?: EmailCampaign) => {
        setCurrentStep(0)
        if (campaign) {

            setEditingId(campaign.id)
            setFormData({
                name: campaign.name,
                subject: campaign.subject,
                smtp_account_id: campaign.smtp_account_id,
                recipients: '',
                body_html: campaign.body_html
            })
        } else {

            setEditingId(null)
            setFormData({
                name: '',
                subject: '',
                smtp_account_id: undefined,
                recipients: '',
                body_html: ''
            })
        }
        setIsWizardVisible(true)
        fetchSmtpAccounts()
    }


    const handleFileUpload = (file: File) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target?.result as string
            if (text) {

                const emails = text
                    .split(/[\n,;]+/)
                    .map(e => e.trim())
                    .filter(e => e && e.includes('@'))
                    .join('\n')

                setFormData(prev => ({
                    ...prev,
                    recipients: prev.recipients ? prev.recipients + '\n' + emails : emails
                }))
                message.success(`Загружено ${emails.split('\n').length} email адресов`)
            }
        }
        reader.readAsText(file)
        return false
    }

    const handleNext = () => {
        if (currentStep === 0) {
            if (!formData.name.trim()) {
                message.error('Введите название рассылки')
                return
            }
            if (!formData.subject.trim()) {
                message.error('Введите тему письма')
                return
            }
            if (!formData.smtp_account_id) {
                message.error('Выберите SMTP сервер')
                return
            }
        }
        if (currentStep === 2) {
            const content = getEditorContent()
            if (!content || content === '<p><br></p>' || content.trim() === '') {
                message.error('Введите содержимое письма')
                return
            }
            setFormData(prev => ({ ...prev, body_html: content }))
        }
        setCurrentStep(currentStep + 1)
    }

    const handlePrev = () => {
        if (currentStep === 2) {
            const content = getEditorContent()
            setFormData(prev => ({ ...prev, body_html: content }))
        }
        setCurrentStep(currentStep - 1)
    }

    const handleFinish = async () => {
        setCreating(true)
        try {
            const recipientsRaw = formData.recipients || ""
            const recipientsList = recipientsRaw
                .split(/[\n,;]+/)
                .map((e: string) => e.trim())
                .filter((e: string) => e && e.includes('@'))

            const payload = {
                name: formData.name,
                subject: formData.subject,
                smtp_account_id: formData.smtp_account_id,
                body_html: formData.body_html || getEditorContent(),
                recipients: recipientsList
            }

            if (editingId) {


                await emailCampaignsApi.update(editingId, payload)
                message.success('Рассылка обновлена!')
            } else {

                await emailCampaignsApi.create(payload)
                message.success('Рассылка создана!')
            }
            setIsWizardVisible(false)
            fetchCampaigns()
        } catch (error: any) {
            const detail = error.response?.data?.detail
            if (typeof detail === 'string') {
                message.error('Ошибка: ' + detail)
            } else if (Array.isArray(detail)) {
                const msgs = detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ')
                message.error('Ошибка валидации: ' + msgs)
            } else {
                message.error('Ошибка: ' + error.message)
            }
        } finally {
            setCreating(false)
        }
    }

    const columns = [
        { title: 'Название', dataIndex: 'name', key: 'name', render: (t: string) => <b>{t}</b> },
        { title: 'Тема письма', dataIndex: 'subject', key: 'subject' },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                let color = 'default'
                let text = status.toUpperCase()
                if (status === 'completed') { color = 'green'; text = 'ЗАВЕРШЕНО' }
                if (status === 'sending' || status === 'processing') { color = 'blue'; text = 'ОТПРАВКА' }
                if (status === 'failed') { color = 'red'; text = 'ОШИБКА' }
                if (status === 'draft') { color = 'orange'; text = 'ЧЕРНОВИК' }
                if (status === 'scheduled') { color = 'cyan'; text = 'В ОЧЕРЕДИ' }
                return <Tag color={color}>{text}</Tag>
            }
        },
        {
            title: 'Статистика',
            key: 'stats',
            render: (_: any, r: EmailCampaign) => (
                <Space size="small">
                    <Tag>Всего: {r.total_recipients}</Tag>
                    <Tag color="success">Отпр: {r.sent_count}</Tag>
                </Space>
            )
        },
        {
            title: 'Создано',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (d: string) => new Date(d).toLocaleDateString('ru-RU')
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, r: EmailCampaign) => (
                <Space>
                    {r.status === 'draft' && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<RocketOutlined />}
                            onClick={() => handleStart(r.id)}
                        >
                            Запустить
                        </Button>
                    )}
                    {r.status === 'completed' && (
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => handleResend(r.id)}
                        >
                            Повторить
                        </Button>
                    )}
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => openWizard(r)}
                        size="small"
                        title="Редактировать"
                    />
                    <Button icon={<DeleteOutlined />} onClick={() => handleDelete(r.id)} size="small" title="Удалить" />
                </Space>
            )
        }
    ]


    const recipientsCount = (formData.recipients || "")
        .split(/[\n,;]+/)
        .filter((e: string) => e.trim().includes('@')).length

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold">Email Рассылки</h1>
                    <p className="text-gray-500">Управление массовыми email-рассылками</p>
                </div>
                <Space>
                    <Button icon={<SettingOutlined />} onClick={() => setIsSmtpModalVisible(true)} size="large">
                        Настройки SMTP
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openWizard()} size="large">
                        Создать Рассылку
                    </Button>
                </Space>
            </div>

            <Card>
                <div className="mb-4 flex justify-end">
                    <Button icon={<CheckCircleOutlined />} onClick={fetchCampaigns}>Обновить статус</Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={campaigns}
                    rowKey="id"
                    loading={loading}
                    locale={{ emptyText: 'Рассылок пока нет. Создайте первую!' }}
                />
            </Card>

            <Modal
                title={<span style={{ fontSize: '18px' }}>{editingId ? '✏️ Редактирование рассылки' : '✉️ Новая рассылка'}</span>}
                open={isWizardVisible}
                onCancel={() => setIsWizardVisible(false)}
                footer={null}
                width={900}
                destroyOnClose={false}
                maskClosable={false}
            >
                <Steps current={currentStep} className="mb-6 mt-4">
                    <Step title="Настройка" icon={<UserOutlined />} />
                    <Step title="Получатели" icon={<UserOutlined />} />
                    <Step title="Дизайн" icon={<FileTextOutlined />} />
                    <Step title="Готово" icon={<CheckCircleOutlined />} />
                </Steps>

                <div className="min-h-[400px]">

                    <div style={{ display: currentStep === 0 ? 'block' : 'none' }} className="py-4">
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">Название рассылки *</label>
                            <Input
                                placeholder="Например: Февральская рассылка"
                                size="large"
                                value={formData.name}
                                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">Тема письма *</label>
                            <Input
                                placeholder="Важное уведомление!"
                                size="large"
                                value={formData.subject}
                                onChange={e => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block mb-2 font-medium">SMTP Сервер *</label>
                            <Select
                                placeholder="Выберите сервер"
                                size="large"
                                style={{ width: '100%' }}
                                value={formData.smtp_account_id}
                                onChange={val => setFormData(prev => ({ ...prev, smtp_account_id: val }))}
                            >
                                {smtpAccounts.map(acc => (
                                    <Select.Option key={acc.id} value={acc.id}>{acc.name} ({acc.host})</Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>


                    <div style={{ display: currentStep === 1 ? 'block' : 'none' }} className="py-4">
                        <div className="mb-3 text-gray-400">
                            Введите email адреса вручную или загрузите из файла (txt, csv).
                        </div>

                        <Dragger
                            accept=".txt,.csv"
                            beforeUpload={handleFileUpload}
                            showUploadList={false}
                            style={{ marginBottom: '16px' }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Перетащите файл или нажмите для загрузки</p>
                            <p className="ant-upload-hint">Поддерживаются .txt и .csv файлы</p>
                        </Dragger>

                        <TextArea
                            rows={10}
                            placeholder={"user1@example.com\nuser2@example.com\nuser3@example.com\n..."}
                            style={{ fontSize: '14px' }}
                            value={formData.recipients}
                            onChange={e => setFormData(prev => ({ ...prev, recipients: e.target.value }))}
                        />
                        <div className="mt-2 text-gray-400 text-sm">
                            Найдено адресов: {recipientsCount}
                        </div>
                    </div>


                    <div style={{ display: currentStep === 2 ? 'block' : 'none' }} className="py-4">
                        <style>{quillStyles}</style>
                        <div className="flex justify-between items-center mb-3">
                            <div className="text-gray-400">Создайте красивое письмо или выберите шаблон:</div>
                            <Dropdown menu={{ items: templates }} trigger={['click']}>
                                <Button type="primary" ghost icon={<BgColorsOutlined />}>
                                    Шаблоны <DownOutlined />
                                </Button>
                            </Dropdown>
                        </div>
                        <div className="custom-quill" style={{ background: '#fff', borderRadius: '8px' }}>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                defaultValue={formData.body_html}
                                modules={modules}
                                formats={formats}
                                placeholder="Начните писать здесь..."
                                style={{ height: '350px', marginBottom: '50px' }}
                            />
                        </div>
                    </div>


                    <div style={{ display: currentStep === 3 ? 'block' : 'none' }} className="py-6 text-center">
                        <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }} />
                        <Title level={3}>{editingId ? 'Готово к сохранению!' : 'Всё готово!'}</Title>
                        <div className="text-left p-5 rounded-lg inline-block border min-w-[350px]" style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <p className="mb-2"><b>📧 Рассылка:</b> {formData.name}</p>
                            <p className="mb-2"><b>📌 Тема:</b> {formData.subject}</p>
                            <p className="mb-2"><b>🖥️ Сервер:</b> {smtpAccounts.find(s => s.id === formData.smtp_account_id)?.name}</p>
                            <p className="mb-0"><b>👥 Получателей:</b> <Tag color="blue">{recipientsCount}</Tag></p>
                        </div>
                        <div className="mt-5 p-4 border rounded text-left max-h-[200px] overflow-auto" style={{ borderColor: '#d9d9d9', background: '#f5f5f5' }}>
                            <div className="text-xs text-gray-500 mb-2">Предпросмотр письма:</div>
                            <div style={{ color: '#333' }} dangerouslySetInnerHTML={{ __html: formData.body_html }} />
                        </div>
                        <p className="mt-4 text-gray-500">
                            {editingId
                                ? 'Нажмите "Сохранить", чтобы применить изменения.'
                                : 'Нажмите "Создать", чтобы сохранить рассылку.'}
                            <br />Запуск производится из списка рассылок.
                        </p>
                    </div>
                </div>

                <Divider />

                <div className="flex justify-between items-center">
                    {currentStep > 0 ? (
                        <Button onClick={handlePrev} size="large">← Назад</Button>
                    ) : <div></div>}

                    {currentStep < 3 && (
                        <Button type="primary" onClick={handleNext} size="large">
                            Далее →
                        </Button>
                    )}
                    {currentStep === 3 && (
                        <Button type="primary" onClick={handleFinish} loading={creating} icon={<RocketOutlined />} size="large" style={{ background: '#52c41a', borderColor: '#52c41a' }}>
                            {editingId ? 'Сохранить' : 'Создать Рассылку'}
                        </Button>
                    )}
                </div>
            </Modal>

            <Modal
                title="⚙️ Настройки SMTP"
                open={isSmtpModalVisible}
                onCancel={() => {
                    setIsSmtpModalVisible(false)
                    fetchSmtpAccounts()
                }}
                footer={null}
                width={1000}
                destroyOnClose
            >
                <SMTPSettings embedded={true} />
            </Modal>
        </div>
    )
}

export default EmailCampaigns
